import { useState } from "react";
import { Plus, X, CheckCircle2, AlertTriangle, Shield } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OpportunityAssessment } from "@/types/pipeline";
import { useUpdateOpportunityAssessment } from "@/hooks/useOpportunityDetail";
import { toast } from "sonner";

interface OpportunityAssessmentCardProps {
  opportunityId: string;
  assessment: OpportunityAssessment | null;
}

const SEVERITY_CONFIG = {
  high: { label: "HIGH", color: "text-red-600 bg-red-50 border-red-200" },
  medium: { label: "MED", color: "text-amber-600 bg-amber-50 border-amber-200" },
  low: { label: "LOW", color: "text-blue-600 bg-blue-50 border-blue-200" },
};

export function OpportunityAssessmentCard({
  opportunityId,
  assessment,
}: OpportunityAssessmentCardProps) {
  const updateAssessment = useUpdateOpportunityAssessment();

  const strengths = assessment?.strengths || [];
  const risks = assessment?.risks || [];

  const [newStrength, setNewStrength] = useState("");
  const [newRisk, setNewRisk] = useState("");
  const [newRiskSeverity, setNewRiskSeverity] = useState<"high" | "medium" | "low">("medium");

  const save = (updated: OpportunityAssessment) => {
    updateAssessment.mutate(
      { id: opportunityId, assessment: updated },
      { onError: () => toast.error("Failed to save assessment") }
    );
  };

  const addStrength = () => {
    if (!newStrength.trim()) return;
    save({ strengths: [...strengths, newStrength.trim()], risks });
    setNewStrength("");
  };

  const removeStrength = (index: number) => {
    save({ strengths: strengths.filter((_, i) => i !== index), risks });
  };

  const addRisk = () => {
    if (!newRisk.trim()) return;
    save({
      strengths,
      risks: [...risks, { text: newRisk.trim(), severity: newRiskSeverity }],
    });
    setNewRisk("");
  };

  const removeRisk = (index: number) => {
    save({ strengths, risks: risks.filter((_, i) => i !== index) });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* What's Working */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-green-700">
            <CheckCircle2 className="h-4 w-4" />
            What's Working
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {strengths.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              No strengths added yet
            </p>
          )}
          {strengths.map((s, i) => (
            <div
              key={i}
              className="flex items-start gap-2 group text-sm bg-green-50 dark:bg-green-950/20 p-2 rounded"
            >
              <Shield className="h-3.5 w-3.5 text-green-600 mt-0.5 shrink-0" />
              <span className="flex-1">{s}</span>
              <button
                onClick={() => removeStrength(i)}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Input
              placeholder="Add a strength..."
              value={newStrength}
              onChange={(e) => setNewStrength(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addStrength()}
              className="h-8 text-sm"
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={addStrength}
              disabled={!newStrength.trim()}
              className="h-8 px-2"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Risks & Blockers */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-4 w-4" />
            Risks & Blockers
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {risks.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              No risks identified
            </p>
          )}
          {risks.map((r, i) => {
            const config = SEVERITY_CONFIG[r.severity];
            return (
              <div
                key={i}
                className="flex items-start gap-2 group text-sm bg-red-50 dark:bg-red-950/20 p-2 rounded"
              >
                <AlertTriangle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
                <span className="flex-1">{r.text}</span>
                <Badge
                  variant="outline"
                  className={`text-[10px] px-1.5 py-0 shrink-0 ${config.color}`}
                >
                  {config.label}
                </Badge>
                <button
                  onClick={() => removeRisk(i)}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
          <div className="flex gap-2 pt-1">
            <Input
              placeholder="Add a risk..."
              value={newRisk}
              onChange={(e) => setNewRisk(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addRisk()}
              className="h-8 text-sm flex-1"
            />
            <Select
              value={newRiskSeverity}
              onValueChange={(v) =>
                setNewRiskSeverity(v as "high" | "medium" | "low")
              }
            >
              <SelectTrigger className="h-8 w-[80px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Med</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="ghost"
              onClick={addRisk}
              disabled={!newRisk.trim()}
              className="h-8 px-2"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
